import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Lenis from "lenis";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import Home from "@/pages/Home";
import Breaks from "@/pages/Breaks";
import BreakDetail from "@/pages/BreakDetail";
import HubPage from "@/pages/HubPage";
import { HeartOfEngland } from "@/pages/Destinations";
import Compare from "@/pages/Compare";
import Experiences from "@/pages/Experiences";
import { HowItWorks, About, Faqs, Reviews, Contact, LegalPage, NotFound } from "@/pages/InfoPages";
import Admin from "@/pages/admin/Admin";
import { ConsentBanner } from "@/components/ConsentBanner";
import { trackPageView } from "@/lib/ga4";

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    trackPageView(pathname);
  }, [pathname]);

  return (
    <>
      <div className="grain-overlay" aria-hidden="true" />
      {!isAdmin && <SiteHeader />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/breaks" element={<Breaks />} />
        <Route path="/breaks/taste-leicestershire" element={<Navigate to="/breaks/taste-foodie-break" replace />} />
        <Route path="/breaks/leicestershire-couples-break" element={<Navigate to="/breaks" replace />} />
        <Route path="/breaks/discover-the-lost-king" element={<Navigate to="/breaks" replace />} />
        <Route path="/breaks/coventry-warwickshire-couples-break" element={<Navigate to="/breaks" replace />} />
        <Route path="/breaks/:slug" element={<BreakDetail />} />
        <Route path="/short-breaks" element={<HubPage slug="short-breaks" />} />
        <Route path="/couples-breaks" element={<HubPage slug="couples-breaks" />} />
        <Route path="/weekend-breaks" element={<HubPage slug="weekend-breaks" />} />
        <Route path="/experience-breaks" element={<HubPage slug="experience-breaks" />} />
        <Route path="/heart-of-england" element={<HeartOfEngland />} />
        <Route path="/destinations" element={<Navigate to="/heart-of-england" replace />} />
        <Route path="/destinations/*" element={<Navigate to="/heart-of-england" replace />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/experiences" element={<Experiences />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/about" element={<About />} />
        <Route path="/faqs" element={<Faqs />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<LegalPage page="privacy" />} />
        <Route path="/cookies" element={<LegalPage page="cookies" />} />
        <Route path="/terms" element={<LegalPage page="terms" />} />
        <Route path="/booking-terms" element={<LegalPage page="booking-terms" />} />
        <Route path="/accessibility" element={<LegalPage page="accessibility" />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isAdmin && <SiteFooter />}
      {!isAdmin && <ConsentBanner />}
      <Toaster position="bottom-right" richColors />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
